import { and, asc, eq, isNull } from "drizzle-orm";
import {
  bill_of_materials,
  bom_component,
  inventory_count,
  inventory_count_line,
  landed_cost,
  landed_cost_line,
  location,
  lot,
  manufacturing_order,
  type PowerSyncDrizzleDB,
  product,
  product_category,
  product_supplier,
  purchase_order,
  purchase_order_line,
  purchase_return,
  purchase_return_line,
  reorder_rule,
  stock_move,
  stock_move_line,
  stock_picking,
  stock_quant,
  uom,
  valuation_layer,
  vendor,
  warehouse,
} from "@stockpilot/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import {
  aggregateQuantDeltas,
  computeLineQuantDeltas,
  type PickingKind,
  type QuantEffect,
  resolveLineLocations,
} from "./operations";
import {
  canConfirm,
  canReceive,
  type IdentifiedPoLine,
  planReceipt,
  resolvePoStateAfterReceipt,
} from "./purchases";
import { type CountLineLike, planCountAdjustments } from "./counts";
import {
  type BomComponentLike,
  canProduce,
  planComponentConsumption,
  producedUnitCost,
} from "./manufacturing";
import { allocateLandedCost, canApplyLandedCost, type LandedCostBase } from "./landed-cost";
import { canConfirmReturn, planReturnEffects, type ReturnLineLike } from "./returns";
import { applyMove, QTY_SCALE } from "./units";
import { applyInbound, type AvcoState, averageUnitCost, valueOf } from "./valuation";

// ─── PRODUCTOS ────────────────────────────────────────────────────────────────

export interface CreateProductInput {
  name: string;
  sku?: string | null;
  kind?: string;
  categoryId?: string | null;
  uomId?: string | null;
  barcode?: string | null;
  stdCost?: number;
  /** Rastreo: 'none' | 'lot' | 'serial' (Stockpilot F4). */
  tracking?: string;
}

export async function createProductMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateProductInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(product).values({
    id,
    organization_id: orgId,
    name: input.name.trim(),
    sku: input.sku?.trim() ?? null,
    kind: input.kind ?? "storable",
    category_id: input.categoryId ?? null,
    uom_id: input.uomId ?? null,
    barcode: input.barcode?.trim() ?? null,
    std_cost: input.stdCost ?? 0,
    active: 1,
    tracking: input.tracking ?? "none",
    // El server tiene `product.is_kit` NOT NULL (migración F5). El write-path de
    // sync envía esta columna explícitamente, así que un NULL local rechaza el
    // batch entero ("null value in column is_kit ... violates not-null"). Un
    // producto creado por el alta básica nunca es un kit → 0.
    is_kit: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateProductMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    sku: string | null;
    kind: string;
    category_id: string | null;
    uom_id: string | null;
    barcode: string | null;
    std_cost: number;
    active: number;
    tracking: string;
  }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await db
    .update(product)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(product.id, id));
}

/**
 * Envía un producto a la papelera (L2, soft-delete). El servidor lo registra
 * como `deleted`; restaurar = limpiar deleted_at. No existe purga (el
 * write-path convierte cualquier DELETE duro en soft-delete igualmente).
 */
export async function deleteProductMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const now = nowISO();
  await db.update(product).set({ deleted_at: now, updated_at: now }).where(eq(product.id, id));
}

/** Restaura un producto desde la papelera. */
export async function restoreProductMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db
    .update(product)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(product.id, id));
}

// ─── CATEGORÍAS / UoM ──────────────────────────────────────────────────────────

export async function createCategoryMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  name: string,
  parentId?: string | null,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(product_category).values({
    id,
    organization_id: orgId,
    parent_id: parentId ?? null,
    name: name.trim(),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function createUomMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  name: string,
  opts?: { category?: string; factor?: number },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(uom).values({
    id,
    organization_id: orgId,
    name: name.trim(),
    category: opts?.category ?? null,
    factor: opts?.factor ?? 1000,
    created_at: now,
    updated_at: now,
  });
  return id;
}

// ─── ALMACENES / UBICACIONES ───────────────────────────────────────────────────

export async function createWarehouseMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  name: string,
  code?: string | null,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(warehouse).values({
    id,
    organization_id: orgId,
    name: name.trim(),
    code: code?.trim() ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function createLocationMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: {
    name: string;
    warehouseId?: string | null;
    parentId?: string | null;
    code?: string | null;
    usage?: string;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(location).values({
    id,
    organization_id: orgId,
    warehouse_id: input.warehouseId ?? null,
    parent_id: input.parentId ?? null,
    name: input.name.trim(),
    code: input.code?.trim() ?? null,
    usage: input.usage ?? "internal",
    created_at: now,
    updated_at: now,
  });
  return id;
}

// ─── EXISTENCIAS: AJUSTE DE INVENTARIO ──────────────────────────────────────────

/**
 * Ajusta el on-hand de un producto en una ubicación. Crea un stock_move
 * (append-only) con el delta y actualiza/crea el stock_quant (proyección
 * derivada). CC-7: bloqueante (await) en una sola transacción.
 *
 * @param targetQty cantidad CONTADA objetivo, en unidades menores (entero).
 * @param reason    motivo del ajuste, OBLIGATORIO (requisito ERP §1b: merma/
 *                  rotura/robo/recuento/otro — el server rechaza moves sin
 *                  motivo). Ver `ADJUSTMENT_REASONS`.
 * @param lotId     lote opcional (F4): scopa el on-hand por (producto×ubicación×lote).
 * @param note      texto libre opcional que acompaña al motivo.
 */
export async function adjustStockMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  productId: string,
  locationId: string,
  targetQty: number,
  reason: string,
  lotId?: string | null,
  note?: string | null,
): Promise<{ moveId: string; newOnHand: number }> {
  const now = nowISO();
  const moveId = generateUUID();
  const lotRef = lotId ?? null;

  const [existing] = await db
    .select({ id: stock_quant.id, qty: stock_quant.qty })
    .from(stock_quant)
    .where(quantMatch(productId, locationId, lotRef));

  const currentOnHand = existing?.qty ?? 0;
  const delta = targetQty - currentOnHand;
  const newOnHand = applyMove(currentOnHand, delta);

  await db.transaction(async (trx) => {
    await trx.insert(stock_move).values({
      id: moveId,
      organization_id: orgId,
      product_id: productId,
      src_location_id: null,
      dst_location_id: locationId,
      qty: delta,
      reason,
      note: note?.trim() || null,
      lot_id: lotRef,
      created_at: now,
      updated_at: now,
    });

    if (existing) {
      await trx
        .update(stock_quant)
        .set({ qty: newOnHand, updated_at: now })
        .where(eq(stock_quant.id, existing.id));
    } else {
      await trx.insert(stock_quant).values({
        id: generateUUID(),
        organization_id: orgId,
        product_id: productId,
        location_id: locationId,
        qty: newOnHand,
        lot_id: lotRef,
        created_at: now,
        updated_at: now,
      });
    }
  });

  return { moveId, newOnHand };
}

/**
 * Filtro de quant por (producto, ubicación, lote). El lote null se trata como
 * "sin lote" (productos sin rastreo): así el on-hand sin rastreo nunca se mezcla
 * con el on-hand por lote del mismo producto/ubicación.
 */
function quantMatch(productId: string, locationId: string, lotId: string | null) {
  return and(
    eq(stock_quant.product_id, productId),
    eq(stock_quant.location_id, locationId),
    lotId === null ? isNull(stock_quant.lot_id) : eq(stock_quant.lot_id, lotId),
  );
}

/** Forma mínima de un move para poder registrar su contramovimiento. */
export interface ReversibleMove {
  id: string;
  organization_id: string | null;
  product_id: string | null;
  src_location_id: string | null;
  dst_location_id: string | null;
  qty: number | null;
  lot_id: string | null;
}

/**
 * CORRECCIÓN de un move confirmado (criterio de dominio §1b): los moves NO se
 * editan, borran ni deshacen — la corrección es un MOVE INVERSO explícito CON
 * motivo, que queda registrado en el historial como cualquier otro movimiento.
 * Invierte origen↔destino (o niega el delta en ajustes de una sola ubicación)
 * y reconcilia la(s) proyección(es) `stock_quant` en la misma transacción.
 */
export async function reverseStockMoveMutation(
  db: PowerSyncDrizzleDB,
  original: ReversibleMove,
  reason: string,
  note?: string | null,
): Promise<{ moveId: string }> {
  const productId = original.product_id;
  // El contramovimiento vive en la MISMA organización que el original (puede
  // ser la org de otro miembro, no la personal del corrector).
  const orgId = original.organization_id;
  if (!productId || !orgId) {
    throw new Error("Cannot reverse a move without product/organization");
  }
  const qty = original.qty ?? 0;
  const lotRef = original.lot_id ?? null;
  const now = nowISO();
  const moveId = generateUUID();

  // Efectos sobre quants: deshacer lo que el move original aplicó.
  // Ajuste (una sola ubicación): delta negado en esa ubicación.
  // Transferencia (src→dst): sale de dst y vuelve a src.
  const effects: { locationId: string; delta: number }[] = [];
  if (original.dst_location_id) {
    effects.push({ locationId: original.dst_location_id, delta: -qty });
  }
  if (original.src_location_id) {
    effects.push({ locationId: original.src_location_id, delta: qty });
  }

  // Forma del move inverso: un AJUSTE (un solo extremo) se invierte negando el
  // delta en la MISMA ubicación (el feed muestra "−5 (recuento)"); una
  // TRANSFERENCIA se invierte intercambiando origen↔destino con la misma qty.
  const isAdjustment = !original.src_location_id || !original.dst_location_id;
  const inverse = isAdjustment
    ? {
        src: original.src_location_id ?? null,
        dst: original.dst_location_id ?? null,
        qty: -qty,
      }
    : {
        src: original.dst_location_id ?? null,
        dst: original.src_location_id ?? null,
        qty,
      };

  await db.transaction(async (trx) => {
    await trx.insert(stock_move).values({
      id: moveId,
      organization_id: orgId,
      product_id: productId,
      src_location_id: inverse.src,
      dst_location_id: inverse.dst,
      qty: inverse.qty,
      reason,
      note: note?.trim() || null,
      lot_id: lotRef,
      created_at: now,
      updated_at: now,
    });

    for (const { locationId, delta } of effects) {
      const [existing] = await trx
        .select({ id: stock_quant.id, qty: stock_quant.qty })
        .from(stock_quant)
        .where(quantMatch(productId, locationId, lotRef));
      if (existing) {
        await trx
          .update(stock_quant)
          .set({ qty: applyMove(existing.qty ?? 0, delta), updated_at: now })
          .where(eq(stock_quant.id, existing.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateUUID(),
          organization_id: orgId,
          product_id: productId,
          location_id: locationId,
          qty: applyMove(0, delta),
          lot_id: lotRef,
          created_at: now,
          updated_at: now,
        });
      }
    }
  });

  return { moveId };
}

// ─── OPERACIONES (Stockpilot F2) ────────────────────────────────────────────

export interface CreatePickingInput {
  kind: PickingKind;
  reference?: string | null;
  warehouseId?: string | null;
  srcLocationId?: string | null;
  dstLocationId?: string | null;
  note?: string | null;
}

/** Crea una operación en estado borrador. */
export async function createPickingMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreatePickingInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(stock_picking).values({
    id,
    organization_id: orgId,
    kind: input.kind,
    state: "draft",
    reference: input.reference?.trim() ?? null,
    warehouse_id: input.warehouseId ?? null,
    src_location_id: input.srcLocationId ?? null,
    dst_location_id: input.dstLocationId ?? null,
    note: input.note?.trim() ?? null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Añade una línea a una operación borrador. Resuelve origen/destino según el
 * tipo de operación y las ubicaciones por defecto del documento.
 */
export async function addPickingLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  picking: {
    id: string;
    kind: PickingKind;
    src_location_id: string | null;
    dst_location_id: string | null;
  },
  productId: string,
  qty: number,
  lotId?: string | null,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  const { srcLocationId, dstLocationId } = resolveLineLocations(
    picking.kind,
    picking.src_location_id,
    picking.dst_location_id,
  );
  await db.insert(stock_move_line).values({
    id,
    organization_id: orgId,
    picking_id: picking.id,
    product_id: productId,
    src_location_id: srcLocationId,
    dst_location_id: dstLocationId,
    qty,
    lot_id: lotId ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina una línea de una operación borrador. */
export async function deletePickingLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(stock_move_line).where(eq(stock_move_line.id, lineId));
}

interface ConfirmablePicking {
  id: string;
  kind: PickingKind;
  state: string | null;
}

interface ConfirmableLine {
  product_id: string | null;
  src_location_id: string | null;
  dst_location_id: string | null;
  qty: number | null;
  lot_id?: string | null;
}

/**
 * Confirma una operación (borrador→hecho): materializa un stock_move por cada
 * efecto de quant de cada línea y actualiza el on-hand derivado (stock_quant).
 * CC-7: bloqueante (await) en una sola transacción. Idempotente: si ya está
 * 'done' no hace nada.
 */
export async function confirmPickingMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  picking: ConfirmablePicking,
  lines: ConfirmableLine[],
): Promise<{ confirmed: boolean; moveCount: number }> {
  if (picking.state === "done") {
    return { confirmed: false, moveCount: 0 };
  }

  // 1) Construir los efectos por (producto, ubicación, lote) a partir de las líneas.
  const effects: QuantEffect[] = [];
  for (const line of lines) {
    const productId = line.product_id;
    const qty = line.qty ?? 0;
    if (!productId || qty <= 0) {
      continue;
    }
    const deltas = computeLineQuantDeltas(
      picking.kind,
      qty,
      line.src_location_id,
      line.dst_location_id,
    );
    for (const d of deltas) {
      effects.push({
        productId,
        locationId: d.locationId,
        delta: d.delta,
        lotId: line.lot_id ?? null,
      });
    }
  }

  const aggregated = aggregateQuantDeltas(effects);
  const now = nowISO();
  let moveCount = 0;

  await db.transaction(async (trx) => {
    for (const { productId, locationId, delta, lotId } of aggregated.values()) {
      if (delta === 0) {
        continue;
      }
      // stock_move append-only (auditoría). dst = ubicación con delta>0,
      // src = ubicación con delta<0 (un único quant por entrada).
      await trx.insert(stock_move).values({
        id: generateUUID(),
        organization_id: orgId,
        product_id: productId,
        src_location_id: delta < 0 ? locationId : null,
        dst_location_id: delta > 0 ? locationId : null,
        qty: delta,
        reason: picking.kind,
        picking_id: picking.id,
        lot_id: lotId,
        created_at: now,
        updated_at: now,
      });
      moveCount += 1;

      const [existing] = await trx
        .select({ id: stock_quant.id, qty: stock_quant.qty })
        .from(stock_quant)
        .where(quantMatch(productId, locationId, lotId));

      if (existing) {
        await trx
          .update(stock_quant)
          .set({ qty: applyMove(existing.qty ?? 0, delta), updated_at: now })
          .where(eq(stock_quant.id, existing.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateUUID(),
          organization_id: orgId,
          product_id: productId,
          location_id: locationId,
          qty: applyMove(0, delta),
          lot_id: lotId,
          created_at: now,
          updated_at: now,
        });
      }
    }

    await trx
      .update(stock_picking)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(stock_picking.id, picking.id));
  });

  return { confirmed: true, moveCount };
}

/**
 * Envía una operación a la papelera (L2, soft-delete). Sus líneas se
 * CONSERVAN (ocultas por el filtro de la operación) y reaparecen al
 * restaurar. Los moves ya materializados por una confirmación no se tocan
 * (append-only): borrar un picking hecho NO deshace su stock.
 */
export async function deletePickingMutation(
  db: PowerSyncDrizzleDB,
  pickingId: string,
): Promise<void> {
  const now = nowISO();
  await db
    .update(stock_picking)
    .set({ deleted_at: now, updated_at: now })
    .where(eq(stock_picking.id, pickingId));
}

/** Restaura una operación desde la papelera (sus líneas reaparecen con ella). */
export async function restorePickingMutation(
  db: PowerSyncDrizzleDB,
  pickingId: string,
): Promise<void> {
  await db
    .update(stock_picking)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(stock_picking.id, pickingId));
}

/** Restaura un almacén desde la papelera. */
export async function restoreWarehouseMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db
    .update(warehouse)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(warehouse.id, id));
}

/** Restaura una ubicación desde la papelera. */
export async function restoreLocationMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db
    .update(location)
    .set({ deleted_at: null, updated_at: nowISO() })
    .where(eq(location.id, id));
}

// ─── REABASTECIMIENTO (Stockpilot F2) ───────────────────────────────────────

export interface CreateReorderRuleInput {
  productId: string;
  warehouseId?: string | null;
  minQty: number;
  maxQty: number;
}

/** Crea una regla de reabastecimiento (mín/máx por producto×almacén). */
export async function createReorderRuleMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateReorderRuleInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(reorder_rule).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    warehouse_id: input.warehouseId ?? null,
    min_qty: input.minQty,
    max_qty: input.maxQty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina una regla de reabastecimiento. */
export async function deleteReorderRuleMutation(
  db: PowerSyncDrizzleDB,
  ruleId: string,
): Promise<void> {
  await db.delete(reorder_rule).where(eq(reorder_rule.id, ruleId));
}

// ─── COMPRAS: PROVEEDORES (Stockpilot F3) ────────────────────────────────────

export interface CreateVendorInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  leadTimeDays?: number;
}

/** Crea un proveedor. */
export async function createVendorMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateVendorInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(vendor).values({
    id,
    organization_id: orgId,
    name: input.name.trim(),
    email: input.email?.trim() ?? null,
    phone: input.phone?.trim() ?? null,
    lead_time_days: input.leadTimeDays ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina un proveedor. */
export async function deleteVendorMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(vendor).where(eq(vendor.id, id));
}

export interface SetSupplierPriceInput {
  productId: string;
  vendorId: string;
  /** Precio de compra por unidad entera, en unidades menores de dinero. */
  price: number;
  vendorCode?: string | null;
  minQty?: number;
  leadTimeDays?: number;
}

/** Fija el precio proveedor-producto (vendor pricelist). */
export async function setSupplierPriceMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: SetSupplierPriceInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(product_supplier).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    vendor_id: input.vendorId,
    vendor_code: input.vendorCode?.trim() ?? null,
    price: input.price,
    min_qty: input.minQty ?? 0,
    lead_time_days: input.leadTimeDays ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina un precio proveedor-producto. */
export async function deleteSupplierPriceMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(product_supplier).where(eq(product_supplier.id, id));
}

// ─── COMPRAS: ÓRDENES DE COMPRA (Stockpilot F3) ──────────────────────────────

export interface CreatePurchaseOrderInput {
  vendorId?: string | null;
  warehouseId?: string | null;
  dstLocationId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Crea una orden de compra en estado borrador. */
export async function createPurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreatePurchaseOrderInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(purchase_order).values({
    id,
    organization_id: orgId,
    vendor_id: input.vendorId ?? null,
    warehouse_id: input.warehouseId ?? null,
    dst_location_id: input.dstLocationId ?? null,
    state: "draft",
    reference: input.reference?.trim() ?? null,
    note: input.note?.trim() ?? null,
    confirmed_at: null,
    received_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Añade una línea a una orden de compra borrador. */
export async function addPurchaseOrderLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  poId: string,
  productId: string,
  qty: number,
  price: number,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(purchase_order_line).values({
    id,
    organization_id: orgId,
    po_id: poId,
    product_id: productId,
    qty,
    price,
    received_qty: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina una línea de una orden de compra borrador. */
export async function deletePurchaseOrderLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(purchase_order_line).where(eq(purchase_order_line.id, lineId));
}

/** Elimina una orden de compra y sus líneas. */
export async function deletePurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  poId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(purchase_order_line).where(eq(purchase_order_line.po_id, poId));
    await trx.delete(purchase_order).where(eq(purchase_order.id, poId));
  });
}

interface PurchaseOrderState {
  id: string;
  state: string | null;
}

/** Confirma una orden de compra (borrador→confirmada). CC-7: bloqueante. */
export async function confirmPurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  poId: string,
  po: PurchaseOrderState,
): Promise<{ confirmed: boolean }> {
  if (!canConfirm(po.state)) {
    return { confirmed: false };
  }
  const now = nowISO();
  await db
    .update(purchase_order)
    .set({ state: "confirmed", confirmed_at: now, updated_at: now })
    .where(eq(purchase_order.id, poId));
  return { confirmed: true };
}

/**
 * Recibe una orden de compra (total o parcial). Genera una recepción
 * (stock_picking tipo 'receipt') con su stock_move por línea, sube el on-hand
 * (stock_quant), crea las capas de valoración AVCO (valuation_layer) al costo de
 * compra de cada línea, y actualiza received_qty. Si queda pendiente, la orden
 * permanece 'confirmed' (BACKORDER); si todo está recibido pasa a 'received'.
 *
 * CC-6: el cálculo AVCO/matching es puro (./valuation, ./purchases).
 * CC-7: todo bloqueante (await) en una sola transacción.
 *
 * @param requested  lineId → cantidad a recibir ahora (unidades menores). Si es
 *                   undefined, recibe TODO lo pendiente de cada línea.
 */
export async function receivePurchaseOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  po: {
    id: string;
    state: string | null;
    warehouse_id: string | null;
    dst_location_id: string | null;
    reference: string | null;
  },
  lines: IdentifiedPoLine[],
  requested?: Map<string, number>,
): Promise<{ received: boolean; pickingId: string | null; backorder: boolean }> {
  if (!canReceive(po.state)) {
    return { received: false, pickingId: null, backorder: false };
  }

  const dstLocationId = po.dst_location_id;
  if (!dstLocationId) {
    // Sin ubicación destino no se puede materializar la recepción.
    return { received: false, pickingId: null, backorder: false };
  }

  const allocations = planReceipt(lines, requested);
  if (allocations.length === 0) {
    return { received: false, pickingId: null, backorder: false };
  }

  const now = nowISO();
  const pickingId = generateUUID();
  // Mapa lineId → cantidad recibida en esta recepción, para actualizar líneas.
  const receivedByLine = new Map<string, number>();
  if (requested) {
    for (const line of lines) {
      const pending = Math.max((line.qty ?? 0) - (line.received_qty ?? 0), 0);
      const ask = Math.min(Math.max(requested.get(line.id) ?? 0, 0), pending);
      if (ask > 0) {
        receivedByLine.set(line.id, ask);
      }
    }
  } else {
    for (const line of lines) {
      const pending = Math.max((line.qty ?? 0) - (line.received_qty ?? 0), 0);
      if (pending > 0) {
        receivedByLine.set(line.id, pending);
      }
    }
  }

  await db.transaction(async (trx) => {
    // 1) Documento de recepción (stock_picking tipo 'receipt') ya confirmado.
    await trx.insert(stock_picking).values({
      id: pickingId,
      organization_id: orgId,
      kind: "receipt",
      state: "done",
      reference: po.reference ?? null,
      warehouse_id: po.warehouse_id ?? null,
      src_location_id: null,
      dst_location_id: dstLocationId,
      note: null,
      done_at: now,
      created_at: now,
      updated_at: now,
    });

    // 2) Estado AVCO acumulado por producto (a partir de capas existentes).
    const avcoByProduct = new Map<string, AvcoState>();

    for (const alloc of allocations) {
      // 2a) stock_move + stock_move_line (entrada a la ubicación destino).
      const moveId = generateUUID();
      await trx.insert(stock_move).values({
        id: moveId,
        organization_id: orgId,
        product_id: alloc.productId,
        src_location_id: null,
        dst_location_id: dstLocationId,
        qty: alloc.qty,
        reason: "receipt",
        picking_id: pickingId,
        created_at: now,
        updated_at: now,
      });
      await trx.insert(stock_move_line).values({
        id: generateUUID(),
        organization_id: orgId,
        picking_id: pickingId,
        product_id: alloc.productId,
        src_location_id: null,
        dst_location_id: dstLocationId,
        qty: alloc.qty,
        created_at: now,
        updated_at: now,
      });

      // 2b) on-hand (stock_quant) en la ubicación destino.
      const [existingQuant] = await trx
        .select({ id: stock_quant.id, qty: stock_quant.qty })
        .from(stock_quant)
        .where(
          and(
            eq(stock_quant.product_id, alloc.productId),
            eq(stock_quant.location_id, dstLocationId),
          ),
        );
      if (existingQuant) {
        await trx
          .update(stock_quant)
          .set({ qty: applyMove(existingQuant.qty ?? 0, alloc.qty), updated_at: now })
          .where(eq(stock_quant.id, existingQuant.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateUUID(),
          organization_id: orgId,
          product_id: alloc.productId,
          location_id: dstLocationId,
          qty: alloc.qty,
          created_at: now,
          updated_at: now,
        });
      }

      // 2c) Capa de valoración AVCO. Estado inicial = suma de capas previas.
      let state = avcoByProduct.get(alloc.productId);
      if (!state) {
        const prior = await trx
          .select({ qty: valuation_layer.qty, value: valuation_layer.value })
          .from(valuation_layer)
          .where(eq(valuation_layer.product_id, alloc.productId))
          .orderBy(asc(valuation_layer.created_at));
        state = prior.reduce<AvcoState>(
          (acc, l) => ({ qty: acc.qty + (l.qty ?? 0), value: acc.value + (l.value ?? 0) }),
          { qty: 0, value: 0 },
        );
      }
      const layer = applyInbound(state, alloc.qty, alloc.unitCost);
      avcoByProduct.set(alloc.productId, layer.state);
      await trx.insert(valuation_layer).values({
        id: generateUUID(),
        organization_id: orgId,
        product_id: alloc.productId,
        move_id: moveId,
        warehouse_id: po.warehouse_id ?? null,
        qty: layer.qty,
        unit_cost: layer.unitCost,
        value: layer.value,
        created_at: now,
        updated_at: now,
      });
    }

    // 3) Actualizar received_qty de las líneas tocadas.
    const linesAfter = lines.map((l) => ({
      ...l,
      received_qty: (l.received_qty ?? 0) + (receivedByLine.get(l.id) ?? 0),
    }));
    for (const line of linesAfter) {
      if (receivedByLine.has(line.id)) {
        await trx
          .update(purchase_order_line)
          .set({ received_qty: line.received_qty, updated_at: now })
          .where(eq(purchase_order_line.id, line.id));
      }
    }

    // 4) Resolver estado de la orden (received vs backorder).
    const nextState = resolvePoStateAfterReceipt(linesAfter);
    await trx
      .update(purchase_order)
      .set({
        state: nextState,
        received_at: nextState === "received" ? now : null,
        updated_at: now,
      })
      .where(eq(purchase_order.id, po.id));
  });

  const totalPending = lines.reduce((acc, l) => {
    const after = (l.received_qty ?? 0) + (receivedByLine.get(l.id) ?? 0);
    return acc + Math.max((l.qty ?? 0) - after, 0);
  }, 0);

  return { received: true, pickingId, backorder: totalPending > 0 };
}

// ─── TRAZABILIDAD: LOTES / SERIES (Stockpilot F4) ────────────────────────────

export interface CreateLotInput {
  productId: string;
  name: string;
  /** Caducidad ISO (YYYY-MM-DD) o null si no caduca. */
  expiryDate?: string | null;
}

/** Crea un lote / número de serie de un producto. */
export async function createLotMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateLotInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(lot).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    name: input.name.trim(),
    expiry_date: input.expiryDate?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Actualiza un lote (nombre / caducidad). */
export async function updateLotMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string; expiry_date: string | null }>,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await db
    .update(lot)
    .set({ ...finalUpdates, updated_at: nowISO() })
    .where(eq(lot.id, id));
}

/** Elimina un lote. */
export async function deleteLotMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(lot).where(eq(lot.id, id));
}

// ─── CONTEO CÍCLICO / INVENTARIO FÍSICO (Stockpilot F4) ──────────────────────

export interface CreateInventoryCountInput {
  locationId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Crea un documento de conteo en estado borrador. */
export async function createInventoryCountMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateInventoryCountInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(inventory_count).values({
    id,
    organization_id: orgId,
    location_id: input.locationId ?? null,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Añade una línea de conteo. Captura el on-hand TEÓRICO vigente del
 * (producto×ubicación×lote) como referencia para el delta al validar.
 */
export async function addInventoryCountLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  count: { id: string; location_id: string | null },
  productId: string,
  countedQty: number,
  lotId?: string | null,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  const lotRef = lotId ?? null;

  let theoretical = 0;
  if (count.location_id) {
    const [existing] = await db
      .select({ qty: stock_quant.qty })
      .from(stock_quant)
      .where(quantMatch(productId, count.location_id, lotRef));
    theoretical = existing?.qty ?? 0;
  }

  await db.insert(inventory_count_line).values({
    id,
    organization_id: orgId,
    count_id: count.id,
    product_id: productId,
    lot_id: lotRef,
    theoretical_qty: theoretical,
    counted_qty: countedQty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina una línea de conteo (borrador). */
export async function deleteInventoryCountLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(inventory_count_line).where(eq(inventory_count_line.id, lineId));
}

interface ValidatableCount {
  id: string;
  state: string | null;
  location_id: string | null;
}

/**
 * Valida un conteo (borrador→validado): por cada línea con diferencia
 * (contado − teórico ≠ 0) materializa un stock_move de ajuste y cuadra el on-hand
 * derivado (stock_quant) por (producto×ubicación×lote). CC-6: el plan de ajustes
 * es puro (./counts). CC-7: bloqueante (await) en una sola transacción.
 * Idempotente: si ya está validado no hace nada.
 */
export async function validateInventoryCountMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  count: ValidatableCount,
  lines: CountLineLike[],
): Promise<{ validated: boolean; moveCount: number }> {
  if (count.state === "done") {
    return { validated: false, moveCount: 0 };
  }
  const locationId = count.location_id;
  if (!locationId) {
    return { validated: false, moveCount: 0 };
  }

  const adjustments = planCountAdjustments(lines);
  const now = nowISO();
  let moveCount = 0;

  await db.transaction(async (trx) => {
    for (const { productId, lotId, delta, newOnHand } of adjustments) {
      // stock_move de ajuste (append-only). dst con delta>0, src con delta<0.
      await trx.insert(stock_move).values({
        id: generateUUID(),
        organization_id: orgId,
        product_id: productId,
        src_location_id: delta < 0 ? locationId : null,
        dst_location_id: delta > 0 ? locationId : null,
        qty: delta,
        reason: "count-adjustment",
        lot_id: lotId,
        created_at: now,
        updated_at: now,
      });
      moveCount += 1;

      const [existing] = await trx
        .select({ id: stock_quant.id })
        .from(stock_quant)
        .where(quantMatch(productId, locationId, lotId));

      if (existing) {
        await trx
          .update(stock_quant)
          .set({ qty: newOnHand, updated_at: now })
          .where(eq(stock_quant.id, existing.id));
      } else {
        await trx.insert(stock_quant).values({
          id: generateUUID(),
          organization_id: orgId,
          product_id: productId,
          location_id: locationId,
          qty: newOnHand,
          lot_id: lotId,
          created_at: now,
          updated_at: now,
        });
      }
    }

    await trx
      .update(inventory_count)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(inventory_count.id, count.id));
  });

  return { validated: true, moveCount };
}

/** Elimina un documento de conteo y sus líneas. */
export async function deleteInventoryCountMutation(
  db: PowerSyncDrizzleDB,
  countId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(inventory_count_line).where(eq(inventory_count_line.count_id, countId));
    await trx.delete(inventory_count).where(eq(inventory_count.id, countId));
  });
}

// ─── MANUFACTURA + LANDED COSTS + DEVOLUCIONES (Stockpilot F5) ────────────────

/**
 * Tipo de la transacción Drizzle (el objeto que recibe el callback de
 * `db.transaction`). Más estrecho que `PowerSyncDrizzleDB` (sin `db`/`watch`),
 * lo justo para los helpers de F5 que corren dentro de una transacción.
 */
type DrizzleTx = Parameters<Parameters<PowerSyncDrizzleDB["transaction"]>[0]>[0];

/**
 * Lee el estado AVCO acumulado de un producto (suma de capas de valoración) bajo
 * una transacción. Reutilizado por todas las rutas de costo de F5.
 */
async function readAvcoState(trx: DrizzleTx, productId: string): Promise<AvcoState> {
  const prior = await trx
    .select({ qty: valuation_layer.qty, value: valuation_layer.value })
    .from(valuation_layer)
    .where(eq(valuation_layer.product_id, productId))
    .orderBy(asc(valuation_layer.created_at));
  return prior.reduce<AvcoState>(
    (acc, l) => ({ qty: acc.qty + (l.qty ?? 0), value: acc.value + (l.value ?? 0) }),
    { qty: 0, value: 0 },
  );
}

/** Aplica un delta al stock_quant de un (producto×ubicación), creándolo si falta. */
async function applyQuantDelta(
  trx: DrizzleTx,
  orgId: string,
  productId: string,
  locationId: string,
  delta: number,
  now: string,
): Promise<void> {
  const [existing] = await trx
    .select({ id: stock_quant.id, qty: stock_quant.qty })
    .from(stock_quant)
    .where(quantMatch(productId, locationId, null));
  if (existing) {
    await trx
      .update(stock_quant)
      .set({ qty: applyMove(existing.qty ?? 0, delta), updated_at: now })
      .where(eq(stock_quant.id, existing.id));
  } else {
    await trx.insert(stock_quant).values({
      id: generateUUID(),
      organization_id: orgId,
      product_id: productId,
      location_id: locationId,
      qty: applyMove(0, delta),
      created_at: now,
      updated_at: now,
    });
  }
}

// ─── BILL OF MATERIALS (BoM) ─────────────────────────────────────────────────

export interface CreateBomInput {
  productId: string;
  /** Cantidad de terminado que produce un lote de receta (unidades menores). */
  qty?: number;
  reference?: string | null;
}

/** Crea una lista de materiales (receta) de un producto terminado. */
export async function createBomMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateBomInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(bill_of_materials).values({
    id,
    organization_id: orgId,
    product_id: input.productId,
    qty: input.qty ?? QTY_SCALE,
    reference: input.reference?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Añade un componente (producto × cantidad) a una BoM. */
export async function addBomComponentMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  bomId: string,
  productId: string,
  qty: number,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(bom_component).values({
    id,
    organization_id: orgId,
    bom_id: bomId,
    product_id: productId,
    qty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina un componente de una BoM. */
export async function deleteBomComponentMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(bom_component).where(eq(bom_component.id, id));
}

/** Elimina una BoM y sus componentes. */
export async function deleteBomMutation(db: PowerSyncDrizzleDB, bomId: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(bom_component).where(eq(bom_component.bom_id, bomId));
    await trx.delete(bill_of_materials).where(eq(bill_of_materials.id, bomId));
  });
}

// ─── ÓRDENES DE FABRICACIÓN ──────────────────────────────────────────────────

export interface CreateManufacturingOrderInput {
  bomId?: string | null;
  productId: string;
  /** Cantidad a fabricar del terminado (unidades menores). */
  qty: number;
  locationId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Crea una orden de fabricación en estado borrador. */
export async function createManufacturingOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateManufacturingOrderInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(manufacturing_order).values({
    id,
    organization_id: orgId,
    bom_id: input.bomId ?? null,
    product_id: input.productId,
    qty: input.qty,
    location_id: input.locationId ?? null,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    produced_cost: 0,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina una orden de fabricación borrador. */
export async function deleteManufacturingOrderMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(manufacturing_order).where(eq(manufacturing_order.id, id));
}

interface ProducibleOrder {
  id: string;
  state: string | null;
  product_id: string | null;
  qty: number | null;
  bom_qty: number;
  location_id: string | null;
  warehouse_id?: string | null;
}

/**
 * Fabrica una orden (borrador→hecho): CONSUME los componentes (stock_move de
 * salida + capa AVCO negativa al costo promedio vigente) y PRODUCE el terminado
 * (stock_move de entrada + capa AVCO positiva) cuyo costo unitario se DERIVA de la
 * suma de costos de componentes consumidos.
 *
 * CC-6: el plan de consumo y el costo derivado son puros (./manufacturing).
 * CC-7: todo bloqueante (await) en una sola transacción. Idempotente: si ya está
 * 'done' no hace nada.
 */
export async function produceManufacturingOrderMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  order: ProducibleOrder,
  components: BomComponentLike[],
): Promise<{ produced: boolean; producedUnitCost: number }> {
  if (!canProduce(order.state)) {
    return { produced: false, producedUnitCost: 0 };
  }
  const productId = order.product_id;
  const locationId = order.location_id;
  const orderQty = order.qty ?? 0;
  if (!productId || !locationId || orderQty <= 0) {
    return { produced: false, producedUnitCost: 0 };
  }

  const consumption = planComponentConsumption(components, orderQty, order.bom_qty);
  const now = nowISO();
  let unitCost = 0;

  await db.transaction(async (trx) => {
    // 1) Consumir componentes: salida + capa AVCO negativa al promedio vigente.
    let consumedValue = 0;
    for (const c of consumption) {
      const state = await readAvcoState(trx, c.productId);
      const avg = averageUnitCost(state);
      const value = valueOf(c.qty, avg);
      consumedValue += value;

      const moveId = generateUUID();
      await trx.insert(stock_move).values({
        id: moveId,
        organization_id: orgId,
        product_id: c.productId,
        src_location_id: locationId,
        dst_location_id: null,
        qty: -c.qty,
        reason: "mfg-consume",
        picking_id: order.id,
        created_at: now,
        updated_at: now,
      });
      await applyQuantDelta(trx, orgId, c.productId, locationId, -c.qty, now);
      await trx.insert(valuation_layer).values({
        id: generateUUID(),
        organization_id: orgId,
        product_id: c.productId,
        move_id: moveId,
        warehouse_id: order.warehouse_id ?? null,
        qty: -c.qty || 0,
        unit_cost: avg,
        value: -value || 0,
        created_at: now,
        updated_at: now,
      });
    }

    // 2) Costo unitario del terminado derivado del valor consumido.
    unitCost = producedUnitCost(consumedValue, orderQty);

    // 3) Producir el terminado: entrada + capa AVCO positiva al costo derivado.
    const finishedMoveId = generateUUID();
    await trx.insert(stock_move).values({
      id: finishedMoveId,
      organization_id: orgId,
      product_id: productId,
      src_location_id: null,
      dst_location_id: locationId,
      qty: orderQty,
      reason: "mfg-produce",
      picking_id: order.id,
      created_at: now,
      updated_at: now,
    });
    await applyQuantDelta(trx, orgId, productId, locationId, orderQty, now);
    await trx.insert(valuation_layer).values({
      id: generateUUID(),
      organization_id: orgId,
      product_id: productId,
      move_id: finishedMoveId,
      warehouse_id: order.warehouse_id ?? null,
      qty: orderQty,
      unit_cost: unitCost,
      value: valueOf(orderQty, unitCost),
      created_at: now,
      updated_at: now,
    });

    await trx
      .update(manufacturing_order)
      .set({ state: "done", produced_cost: unitCost, done_at: now, updated_at: now })
      .where(eq(manufacturing_order.id, order.id));
  });

  return { produced: true, producedUnitCost: unitCost };
}

// ─── LANDED COSTS ────────────────────────────────────────────────────────────

export interface CreateLandedCostInput {
  pickingId: string;
  /** Costo adicional total a repartir (unidades menores de dinero). */
  amount: number;
  reference?: string | null;
  note?: string | null;
}

/** Crea un landed cost en estado borrador sobre una recepción. */
export async function createLandedCostMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreateLandedCostInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(landed_cost).values({
    id,
    organization_id: orgId,
    picking_id: input.pickingId,
    amount: input.amount,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina un landed cost borrador. */
export async function deleteLandedCostMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(landed_cost).where(eq(landed_cost.id, id));
}

interface ApplicableLandedCost {
  id: string;
  state: string | null;
  picking_id: string | null;
  amount: number | null;
  warehouse_id?: string | null;
}

/**
 * Aplica un landed cost (borrador→aplicado): reparte el costo adicional sobre los
 * productos de la recepción PROPORCIONALMENTE a su valor recibido y crea, por
 * cada producto, una capa de valoración de SOLO-VALOR (qty=0) que revaloriza el
 * inventario (sube el costo promedio móvil sin tocar el on-hand).
 *
 * CC-6: el reparto es puro (./landed-cost). CC-7: bloqueante en una transacción.
 * Idempotente: si ya está 'done' no hace nada.
 */
export async function applyLandedCostMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  lc: ApplicableLandedCost,
): Promise<{ applied: boolean; allocationCount: number }> {
  if (!canApplyLandedCost(lc.state)) {
    return { applied: false, allocationCount: 0 };
  }
  const pickingId = lc.picking_id;
  const amount = lc.amount ?? 0;
  if (!pickingId || amount === 0) {
    return { applied: false, allocationCount: 0 };
  }

  // Base de reparto: valor recibido por producto en esa recepción (capas cuyo
  // move pertenece al picking). Se agrega por producto.
  const layers = await db
    .select({
      product_id: valuation_layer.product_id,
      value: valuation_layer.value,
      move_id: valuation_layer.move_id,
    })
    .from(valuation_layer)
    .innerJoin(stock_move, eq(valuation_layer.move_id, stock_move.id))
    .where(eq(stock_move.picking_id, pickingId));

  const byProduct = new Map<string, number>();
  for (const l of layers) {
    const pid = l.product_id;
    if (!pid) {
      continue;
    }
    byProduct.set(pid, (byProduct.get(pid) ?? 0) + (l.value ?? 0));
  }
  const bases: LandedCostBase[] = [...byProduct.entries()].map(([productId, receiptValue]) => ({
    productId,
    receiptValue,
  }));
  if (bases.length === 0) {
    return { applied: false, allocationCount: 0 };
  }

  const allocations = allocateLandedCost(amount, bases);
  const now = nowISO();
  let allocationCount = 0;

  await db.transaction(async (trx) => {
    for (const alloc of allocations) {
      if (alloc.amount === 0) {
        continue;
      }
      // Capa de revaloración: qty=0 (no toca on-hand), value=porción imputada.
      const layerId = generateUUID();
      await trx.insert(valuation_layer).values({
        id: layerId,
        organization_id: orgId,
        product_id: alloc.productId,
        move_id: null,
        warehouse_id: lc.warehouse_id ?? null,
        qty: 0,
        unit_cost: 0,
        value: alloc.amount,
        created_at: now,
        updated_at: now,
      });
      await trx.insert(landed_cost_line).values({
        id: generateUUID(),
        organization_id: orgId,
        landed_cost_id: lc.id,
        product_id: alloc.productId,
        valuation_layer_id: layerId,
        amount: alloc.amount,
        created_at: now,
        updated_at: now,
      });
      allocationCount += 1;
    }

    await trx
      .update(landed_cost)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(landed_cost.id, lc.id));
  });

  return { applied: true, allocationCount };
}

// ─── DEVOLUCIONES A PROVEEDOR ────────────────────────────────────────────────

export interface CreatePurchaseReturnInput {
  poId?: string | null;
  srcLocationId?: string | null;
  warehouseId?: string | null;
  reference?: string | null;
  note?: string | null;
}

/** Crea una devolución a proveedor en estado borrador. */
export async function createPurchaseReturnMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  input: CreatePurchaseReturnInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(purchase_return).values({
    id,
    organization_id: orgId,
    po_id: input.poId ?? null,
    src_location_id: input.srcLocationId ?? null,
    warehouse_id: input.warehouseId ?? null,
    state: "draft",
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
    done_at: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Añade una línea (producto × cantidad) a una devolución borrador. */
export async function addPurchaseReturnLineMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  returnId: string,
  productId: string,
  qty: number,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(purchase_return_line).values({
    id,
    organization_id: orgId,
    return_id: returnId,
    product_id: productId,
    qty,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Elimina una línea de una devolución borrador. */
export async function deletePurchaseReturnLineMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
): Promise<void> {
  await db.delete(purchase_return_line).where(eq(purchase_return_line.id, lineId));
}

/** Elimina una devolución y sus líneas. */
export async function deletePurchaseReturnMutation(
  db: PowerSyncDrizzleDB,
  returnId: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(purchase_return_line).where(eq(purchase_return_line.return_id, returnId));
    await trx.delete(purchase_return).where(eq(purchase_return.id, returnId));
  });
}

interface ConfirmableReturn {
  id: string;
  state: string | null;
  src_location_id: string | null;
  warehouse_id: string | null;
}

/**
 * Confirma una devolución a proveedor (borrador→hecho): por cada línea retira
 * stock (stock_move de salida) del on-hand y registra una capa de valoración
 * NEGATIVA al costo promedio móvil vigente. CC-6: el plan es puro (./returns).
 * CC-7: bloqueante en una transacción. Idempotente: si ya está 'done' no hace nada.
 */
export async function confirmPurchaseReturnMutation(
  db: PowerSyncDrizzleDB,
  orgId: string,
  ret: ConfirmableReturn,
  lines: ReturnLineLike[],
): Promise<{ confirmed: boolean; moveCount: number }> {
  if (!canConfirmReturn(ret.state)) {
    return { confirmed: false, moveCount: 0 };
  }
  const locationId = ret.src_location_id;
  if (!locationId) {
    return { confirmed: false, moveCount: 0 };
  }

  const effects = planReturnEffects(lines);
  const now = nowISO();
  let moveCount = 0;

  await db.transaction(async (trx) => {
    for (const eff of effects) {
      const state = await readAvcoState(trx, eff.productId);
      const avg = averageUnitCost(state);
      const value = valueOf(eff.qty, avg);

      const moveId = generateUUID();
      await trx.insert(stock_move).values({
        id: moveId,
        organization_id: orgId,
        product_id: eff.productId,
        src_location_id: locationId,
        dst_location_id: null,
        qty: -eff.qty,
        reason: "purchase-return",
        picking_id: ret.id,
        created_at: now,
        updated_at: now,
      });
      await applyQuantDelta(trx, orgId, eff.productId, locationId, -eff.qty, now);
      await trx.insert(valuation_layer).values({
        id: generateUUID(),
        organization_id: orgId,
        product_id: eff.productId,
        move_id: moveId,
        warehouse_id: ret.warehouse_id ?? null,
        qty: -eff.qty || 0,
        unit_cost: avg,
        value: -value || 0,
        created_at: now,
        updated_at: now,
      });
      moveCount += 1;
    }

    await trx
      .update(purchase_return)
      .set({ state: "done", done_at: now, updated_at: now })
      .where(eq(purchase_return.id, ret.id));
  });

  return { confirmed: true, moveCount };
}
